import isEqual from 'lodash/isEqual'
import uniq from 'lodash/uniq'
import { v4 } from 'uuid'

import { excludeAbortError } from '@app/errors/AbortError'

import { createSelector } from '@app/utils/createSelector'
import { mergeAbortSignals } from '@app/utils/mergeAbortSignals'
import { errorToFetchDataError } from '@app/utils/performFetchData'
import type { SearchParamsManager } from '@app/utils/search/SearchParamsManager'

import { getAbortSignal, withAbortSignal } from '@app/packages/abortContext/actions'
import type { SearchPageMeta } from '@app/packages/api/v2/actions/search'
import type { SitterSearchQuery } from '@app/packages/api/v2/types/params'
import { asError } from '@app/packages/asError/asError'

import { getAbortControllerService } from '@app/store/actions/contexts/abortControllerService'
import { getCookies } from '@app/store/actions/contexts/cookies'
import { createReduxSlice } from '@app/store/redux_slice'
import { markUsersLoadedSlice } from '@app/store/slices/users'
import { createThunk } from '@app/store/thunk'

import { searchCookie } from './params'

export type SearchResultsState = {
  ids: string[]
  /** updates with every new search that starts with page 1 */
  searchId: string
  total: number
  loading: boolean
  page?: number
  continuation?: { query: SitterSearchQuery } | null
  error?: unknown
}

const searchResultsSlice = createReduxSlice<SearchResultsState>('search_results')

const startSearchSlice = () => searchResultsSlice.update(state => ({ ...(state ?? DEFAULT_STATE), loading: true }))

const failSearchSlice = (error: Error) => searchResultsSlice.update(state => ({ ...(state ?? DEFAULT_STATE), loading: false, error: excludeAbortError(error) }))

const setSearchResultsSlice = (query: SitterSearchQuery, resp: { data: { id: string }[]; meta: SearchPageMeta }) =>
  searchResultsSlice.update(state => {
    const base = state ?? DEFAULT_STATE
    const ids = resp.data.map(u => u.id)
    const firstPage = query.page === 1
    return {
      ...base,
      ids: firstPage ? ids : uniq([...base.ids, ...ids]),
      searchId: firstPage ? v4() : base.searchId,
      loading: false,
      page: resp.meta.current_page,
      total: resp.meta.total,
      continuation: resp.meta.current_page < resp.meta.total_pages ? { query: { ...query, page: resp.meta.current_page + 1 } } : null,
    }
  })

export const searchMetaSelector = createSelector([searchResultsSlice.selector], state => state ?? DEFAULT_STATE)
export const searchIdsSelector = createSelector([searchResultsSlice.selector], state => state?.ids ?? EMPTY_IDS)

export function fetchSearchResults(request: SearchParamsManager | 'next') {
  return createThunk(async (dispatch, getState) => {
    const [{ getSitterSearch }, { callV2Api }] = await Promise.all([import('@app/packages/api/v2/actions/search'), import('@app/store/actions/api')])
    const abort = dispatch(getAbortControllerService())
    const cookies = dispatch(getCookies())
    if (request === 'next' && searchMetaSelector(getState()).loading) return
    if (request === 'next' && searchMetaSelector(getState()).continuation === null) return

    const abortSignal = dispatch(getAbortSignal())
    const abortController = abort.createAbortController(SITTER_REQUEST_LABEL)
    const signal = abortSignal ? mergeAbortSignals(abortController.signal, abortSignal) : abortController.signal

    const meta = searchMetaSelector(getState())

    const apiquery = request === 'next' ? meta.continuation?.query : await request.toApiQuery({ page: 1, per_page: SITTER_SEARCH_PER_PAGE })
    if (!apiquery) return

    const cookieParams = await searchCookie.get(cookies)
    if (request !== 'next' && !isEqual(cookieParams, request.params)) {
      await searchCookie.set(cookies, request.params)
    }

    dispatch(startSearchSlice())
    const result = await dispatch(withAbortSignal(signal, callV2Api(getSitterSearch(apiquery)))).catch((e: unknown) => {
      const error = asError(e)
      dispatch(failSearchSlice(error))
      throw dispatch(errorToFetchDataError(error, 'Sitter search fetch failed'))
    })
    dispatch(setSearchResultsSlice(apiquery, result))
    dispatch(markUsersLoadedSlice(result.data.map(u => u.attributes.token)))

    return result
  })
}

const DEFAULT_STATE: SearchResultsState = { ids: [], searchId: v4(), total: 0, loading: false }
const EMPTY_IDS: string[] = []

const SITTER_SEARCH_PER_PAGE = 20
const SITTER_REQUEST_LABEL = 'search-request'
